import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_KEYS } from "@/lib/categories";
import { Radar as RadarIcon, Flame, Check, Navigation, Music, Plus, X, Loader2 } from "lucide-react";

const DISTANCES = [25, 50, 75, 100];

export default function Radar() {
  const { t, tCategory } = useLang();
  const { user, loading } = useAuth();
  const [cats, setCats] = useState([]);
  const [maxKm, setMaxKm] = useState(50);
  const [coords, setCoords] = useState(null);
  const [locState, setLocState] = useState("idle");
  const [saving, setSaving] = useState(false);
  const [artistQuery, setArtistQuery] = useState("");

  const { data: artists = [] } = useQuery({ queryKey: ["artists"], queryFn: async () => (await api.get("/artists")).data });
  const { data: myArtists = [], refetch: refetchArtists } = useQuery({ queryKey: ["myArtists"], queryFn: async () => (await api.get("/me/artists")).data, enabled: !!user });
  const { data: feed = [], refetch: refetchFeed } = useQuery({
    queryKey: ["radarFeed", coords?.lat, coords?.lng],
    queryFn: async () => (await api.get("/me/radar", { params: coords ? { lat: coords.lat, lng: coords.lng } : {} })).data,
    enabled: !!user,
  });

  useEffect(() => {
    if (user) {
      setCats(user.radar_categories || []);
      setMaxKm(user.radar_max_km || 50);
      if (user.radar_lat && user.radar_lng) { setCoords({ lat: user.radar_lat, lng: user.radar_lng }); setLocState("ok"); }
    }
  }, [user]);

  const locate = () => {
    if (!navigator.geolocation) { setLocState("denied"); return; }
    setLocState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocState("ok"); toast.success(t("radar_located")); },
      () => setLocState("denied"),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  };

  const toggleCat = (c) => setCats((a) => (a.includes(c) ? a.filter((x) => x !== c) : [...a, c]));

  const save = async () => {
    setSaving(true);
    try {
      const body = { radar_categories: cats, radar_max_km: maxKm };
      if (coords) { body.radar_lat = coords.lat; body.radar_lng = coords.lng; }
      await api.put("/me/settings", body);
      toast.success(t("radar_saved"));
      refetchFeed();
    } catch { toast.error(t("aw_error")); } finally { setSaving(false); }
  };

  const addArtist = async (name) => {
    try { await api.post("/me/artists", { artist: name }); refetchArtists(); refetchFeed(); setArtistQuery(""); }
    catch { toast.error(t("aw_error")); }
  };
  const removeArtist = async (name) => {
    try { await api.delete(`/me/artists/${encodeURIComponent(name)}`); refetchArtists(); refetchFeed(); }
    catch { toast.error(t("aw_error")); }
  };

  const suggestions = useMemo(() => {
    const q = artistQuery.trim().toLowerCase();
    if (!q) return [];
    return artists.filter((a) => a.name.toLowerCase().includes(q) && !myArtists.includes(a.name)).slice(0, 6);
  }, [artistQuery, artists, myArtists]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/radar")}`;
  };

  if (loading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!user) return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <RadarIcon className="w-12 h-12 text-amber-500 mx-auto mb-6" />
      <h1 className="font-serif text-2xl text-amber-100 mb-4">{t("radar_title")}</h1>
      <p className="text-stone-400 mb-8">{t("radar_login_desc")}</p>
      <button data-testid="radar-login-btn" onClick={handleLogin} className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md">{t("login_google")}</button>
    </div>
  );

  const chip = (active) => `px-4 py-2 rounded-full text-sm border transition-all ${active ? "bg-amber-600 text-stone-950 font-semibold border-amber-400" : "bg-[#121418] text-stone-300 border-[#272A34] hover:border-amber-500/40"}`;

  return (
    <>
      <Seo title={t("radar_title")} canonicalPath="/radar" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-3"><RadarIcon className="w-7 h-7 text-amber-500" /><h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">{t("radar_title")}</h1></div>
        <p className="text-stone-400 mb-10 max-w-2xl">{t("radar_desc")}</p>

        <div className="bg-[#121418] border border-[#272A34] rounded-xl p-6 sm:p-8 space-y-8" data-testid="radar-prefs">
          {/* Categories */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-4">{t("radar_categories")}</h3>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_KEYS.map((c) => <button key={c} data-testid={`radar-cat-${c}`} onClick={() => toggleCat(c)} className={chip(cats.includes(c))}>{tCategory(c)}</button>)}
            </div>
          </div>

          {/* Distance + location */}
          <div className="pt-2 border-t border-[#272A34]">
            <h3 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-4 flex items-center gap-2"><Navigation className="w-4 h-4" /> {t("radar_distance")}</h3>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {DISTANCES.map((d) => <button key={d} data-testid={`radar-dist-${d}`} onClick={() => setMaxKm(d)} className={chip(maxKm === d)}>{d} km</button>)}
            </div>
            {locState === "ok" ? (
              <p className="text-xs text-emerald-400/90 flex items-center gap-1.5" data-testid="radar-loc-ok"><Check className="w-3.5 h-3.5" /> {t("radar_loc_set")}</p>
            ) : (
              <button data-testid="radar-locate-btn" onClick={locate} disabled={locState === "locating"} className="inline-flex items-center gap-2 bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/50 px-5 py-2.5 rounded-md text-sm transition-all">
                {locState === "locating" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />} {t("radar_use_location")}
              </button>
            )}
            {locState === "denied" && <p className="text-xs text-stone-500 mt-2" data-testid="radar-loc-denied">{t("nearyou_denied")}</p>}
          </div>

          {/* Followed artists */}
          <div className="pt-2 border-t border-[#272A34]">
            <h3 className="font-mono text-xs uppercase tracking-wider text-indigo-300/90 mb-4 flex items-center gap-2"><Music className="w-4 h-4" /> {t("radar_artists")}</h3>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="radar-my-artists">
              {myArtists.length === 0 && <span className="text-stone-500 text-sm">{t("radar_no_artists")}</span>}
              {myArtists.map((a) => (
                <span key={a} data-testid={`radar-artist-chip-${a}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-indigo-950/50 text-indigo-200 border border-indigo-800/50">
                  {a}
                  <button data-testid={`radar-artist-remove-${a}`} onClick={() => removeArtist(a)} className="hover:text-white"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
            <div className="relative max-w-md">
              <input
                data-testid="radar-artist-search"
                value={artistQuery}
                onChange={(e) => setArtistQuery(e.target.value)}
                placeholder={t("radar_artist_search")}
                className="w-full bg-[#0B0C0E] border border-[#272A34] rounded-md px-4 py-2.5 text-sm text-stone-200 focus:border-indigo-500/50 outline-none"
              />
              {suggestions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-[#181A20] border border-[#272A34] rounded-md shadow-xl overflow-hidden" data-testid="radar-artist-suggestions">
                  {suggestions.map((a) => (
                    <button key={a.name} data-testid={`radar-artist-add-${a.name}`} onClick={() => addArtist(a.name)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D] text-left">
                      <Plus className="w-3.5 h-3.5 text-indigo-400" /> {a.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button data-testid="radar-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 font-semibold px-7 py-3 rounded-md shadow-lg transition-all disabled:opacity-60 border border-amber-400/30">
            <Check className="w-4 h-4" /> {saving ? "…" : t("radar_save")}
          </button>
        </div>

        {/* Feed */}
        <section className="mt-14">
          <h2 className="font-serif text-2xl text-stone-100 mb-6 border-b border-amber-900/30 pb-4 flex items-center gap-2"><Flame className="w-5 h-5 text-amber-500" /> {t("radar_feed")} ({feed.length})</h2>
          {feed.length === 0 ? (
            <div className="text-center py-16 text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="radar-feed-empty">{t("radar_feed_empty")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="radar-feed">
              {feed.map((e, i) => <EventCard key={e.slug} event={e} index={i} distanceKm={e._distance_km ?? null} />)}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
